import React, {createContext, useContext, useEffect, useMemo, useReducer, useRef} from 'react';
import {notification} from 'antd';
import {useTranslation} from 'react-i18next';
import api from '../components/api';
import {activityActionHref, requestAppNavigation} from '../components/helpers/AppNavigationBridge';
import useCurrentUser from '../components/helpers/useCurrentUser';
import {SessionContext} from './SessionContext';
import {BACKGROUND_ACTIVITY_HISTORY_ENABLED} from '../components/helpers/backgroundActivityRelease';
import {
    activityFromCableEvent,
    activityIsTerminal,
    activityNotificationMethod,
    activityNotificationTitle,
    backgroundActivitiesReducer,
    initialActivitiesState,
    reconcileActivityUpdates,
    unnotifiedTerminalActivities,
    visibleActivities,
} from './backgroundActivities';

const StateContext = createContext(initialActivitiesState);
const ActionsContext = createContext();

const storedIds = key => {
    try {
        return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (_error) {
        return [];
    }
};

const storeIds = (key, ids) => {
    try {
        localStorage.setItem(key, JSON.stringify(Array.from(new Set(ids))));
    } catch (_error) {
        // Activity state remains available for this session when storage is unavailable.
    }
};

const storedFlag = key => {
    try {
        return localStorage.getItem(key) === 'true';
    } catch (_error) {
        return false;
    }
};

const storeFlag = key => {
    try {
        localStorage.setItem(key, 'true');
    } catch (_error) {
        // The in-memory hydration marker still prevents duplicate baselines in this mount.
    }
};

export const BackgroundActivitiesProvider = ({children}) => {
    const {t} = useTranslation();
    const {state: sessionState} = useContext(SessionContext);
    const currentOrg = sessionState?.currentOrg;
    const currentUser = useCurrentUser();
    const currentOrgIdRef = useRef(currentOrg?.id);
    currentOrgIdRef.current = currentOrg?.id;
    const loadRequestIdRef = useRef(0);
    const hydratedStoragePrefixesRef = useRef(new Set());
    const liveBeforeHydrationRef = useRef(new Map());
    const refreshStatusTimeoutRef = useRef();
    const [state, dispatch] = useReducer(backgroundActivitiesReducer, initialActivitiesState);
    const stateRef = useRef(state);
    stateRef.current = state;
    const reduceAndDispatch = action => {
        stateRef.current = backgroundActivitiesReducer(stateRef.current, action);
        dispatch(action);
    };

    const storagePrefix = `background-activities:${currentOrg?.id || 'none'}:${currentUser?.id || 'none'}`;
    const storagePrefixRef = useRef(storagePrefix);
    storagePrefixRef.current = storagePrefix;
    const dismissedKey = `${storagePrefix}:dismissed`;
    const initializedKey = `${storagePrefix}:initialized`;
    const notifiedKey = `${storagePrefix}:notified`;
    const seenKey = `${storagePrefix}:seen`;

    const reviveActiveActivities = activities => {
        const activeIds = new Set(activities.filter(activity => !activity.terminal).map(activity => activity.id));
        if (!activeIds.size) return;

        [dismissedKey, notifiedKey, seenKey].forEach(key => {
            storeIds(key, storedIds(key).filter(id => !activeIds.has(id)));
        });
    };

    const announce = activities => {
        const notifiedIds = storedIds(notifiedKey);
        const unnotified = unnotifiedTerminalActivities(activities, notifiedIds);
        unnotified.forEach(activity => {
            const method = activityNotificationMethod(activity);
            const actionHref = activityActionHref(activity.action_path, currentOrg?.slug);
            notification[method]({
                key: `background-activity-${activity.id}`,
                message: activityNotificationTitle(activity, t),
                description: (
                    <div>
                        <div>{activity.summary}</div>
                        {actionHref && (
                            <a href={actionHref} onClick={event => requestAppNavigation(event, actionHref)}>
                                {activity.action_label || t('view-activity', 'View activity')}
                            </a>
                        )}
                    </div>
                ),
            });
        });
        if (unnotified.length) storeIds(notifiedKey, [...notifiedIds, ...unnotified.map(activity => activity.id)]);
    };

    const load = ({notify = false} = {}) => {
        if (!BACKGROUND_ACTIVITY_HISTORY_ENABLED) return Promise.resolve([]);
        if (!currentOrg || !currentUser) return Promise.resolve([]);
        const requestId = ++loadRequestIdRef.current;
        const requestedStoragePrefix = storagePrefix;
        const baselineById = stateRef.current.byId;
        reduceAndDispatch({type: 'loadStarted'});
        return api.get('/api/background_activities').then(({data}) => {
            if (storagePrefixRef.current !== requestedStoragePrefix || loadRequestIdRef.current !== requestId) return [];
            const activities = Array.isArray(data) ? data : data.activities || [];
            const systemStatus = Array.isArray(data) ? null : data.system_status;
            const acceptedActivities = reconcileActivityUpdates(stateRef.current.byId, activities);
            const firstHydration = !hydratedStoragePrefixesRef.current.has(requestedStoragePrefix) &&
                !storedFlag(initializedKey);
            const liveBeforeHydration = liveBeforeHydrationRef.current.get(requestedStoragePrefix) || new Set();
            const historicalTerminalIds = firstHydration
                ? activities
                    .filter(activity => activityIsTerminal(activity) && !liveBeforeHydration.has(activity.id))
                    .map(activity => activity.id)
                : [];
            if (firstHydration) {
                hydratedStoragePrefixesRef.current.add(requestedStoragePrefix);
                storeFlag(initializedKey);
                liveBeforeHydrationRef.current.delete(requestedStoragePrefix);
                if (historicalTerminalIds.length) {
                    storeIds(seenKey, [...storedIds(seenKey), ...historicalTerminalIds]);
                    storeIds(notifiedKey, [...storedIds(notifiedKey), ...historicalTerminalIds]);
                    reduceAndDispatch({type: 'markSeen', ids: historicalTerminalIds});
                }
            } else if (!hydratedStoragePrefixesRef.current.has(requestedStoragePrefix)) {
                hydratedStoragePrefixesRef.current.add(requestedStoragePrefix);
                liveBeforeHydrationRef.current.delete(requestedStoragePrefix);
            }
            reviveActiveActivities(acceptedActivities);
            if (notify) announce(acceptedActivities.filter(activity => !historicalTerminalIds.includes(activity.id)));
            else storeIds(notifiedKey, [
                ...storedIds(notifiedKey),
                ...acceptedActivities.filter(activity => activity.terminal).map(activity => activity.id),
            ]);
            reduceAndDispatch({type: 'replace', activities, baselineById});
            reduceAndDispatch({type: 'loadSucceeded', systemStatus});
            return activities;
        }).catch(error => {
            if (storagePrefixRef.current !== requestedStoragePrefix || loadRequestIdRef.current !== requestId) return [];
            console.error('Failed to load background activities:', error);
            reduceAndDispatch({type: 'loadFailed', error});
            throw error;
        });
    };

    useEffect(() => {
        reduceAndDispatch({
            type: 'reset',
            dismissedIds: storedIds(dismissedKey),
            seenIds: storedIds(seenKey),
        });
        load().catch(() => {});
    }, [currentOrg?.id, currentUser?.id]);

    useEffect(() => {
        const onRefreshStatus = event => {
            if (String(event.detail?.organizationId) !== String(currentOrgIdRef.current)) return;
            clearTimeout(refreshStatusTimeoutRef.current);
            reduceAndDispatch({
                type: 'refreshStatus',
                status: event.detail?.status,
                error: event.detail?.error,
            });
            if (event.detail?.status === 'up_to_date') {
                refreshStatusTimeoutRef.current = setTimeout(() => {
                    reduceAndDispatch({type: 'refreshStatus', status: 'idle'});
                }, 3000);
            }
        };
        window.addEventListener('mediagraph:refreshStatus', onRefreshStatus);
        return () => {
            clearTimeout(refreshStatusTimeoutRef.current);
            window.removeEventListener('mediagraph:refreshStatus', onRefreshStatus);
        };
    }, []);

    const actions = useMemo(() => ({
        receiveCableEvent(data) {
            if (!BACKGROUND_ACTIVITY_HISTORY_ENABLED) return;
            const activity = activityFromCableEvent(data);
            if (!activity) return;
            if (String(activity.organization_id) !== String(currentOrgIdRef.current)) return;
            const acceptedActivities = reconcileActivityUpdates(stateRef.current.byId, [activity]);
            if (!acceptedActivities.length) return;
            const currentStoragePrefix = storagePrefixRef.current;
            if (!hydratedStoragePrefixesRef.current.has(currentStoragePrefix)) {
                const liveIds = liveBeforeHydrationRef.current.get(currentStoragePrefix) || new Set();
                liveIds.add(activity.id);
                liveBeforeHydrationRef.current.set(currentStoragePrefix, liveIds);
            }
            reviveActiveActivities(acceptedActivities);
            announce(acceptedActivities);
            reduceAndDispatch({type: 'merge', activities: acceptedActivities});
        },
        dismiss(id) {
            const ids = [...stateRef.current.dismissedIds, id];
            storeIds(dismissedKey, ids);
            reduceAndDispatch({type: 'dismiss', id});
        },
        markSeen() {
            const ids = Object.values(stateRef.current.byId)
                .filter(activity => activity.terminal)
                .map(activity => activity.id);
            storeIds(seenKey, [...stateRef.current.seenIds, ...ids]);
            reduceAndDispatch({type: 'markSeen', ids});
        },
        clearTerminal() {
            const ids = Object.values(stateRef.current.byId).filter(activity => activity.terminal).map(activity => activity.id);
            storeIds(dismissedKey, [...stateRef.current.dismissedIds, ...ids]);
            reduceAndDispatch({type: 'clearTerminal'});
        },
        refresh: () => load({notify: true}),
    }), [storagePrefix, t]);

    const scopedState = useMemo(() => ({
        ...state,
        byId: Object.fromEntries(Object.entries(state.byId).filter(([, activity]) =>
            String(activity.organization_id) === String(currentOrg?.id)
        )),
    }), [state, currentOrg?.id]);

    return (
        <StateContext.Provider value={scopedState}>
            <ActionsContext.Provider value={actions}>{children}</ActionsContext.Provider>
        </StateContext.Provider>
    );
};

export const useBackgroundActivities = () => {
    const state = useContext(StateContext);
    const actions = useContext(ActionsContext);
    if (!actions) throw new Error('useBackgroundActivities must be used within BackgroundActivitiesProvider');
    return {...state, activities: visibleActivities(state), ...actions};
};
